import { List, Map, Set } from "immutable";
import { last, map } from "lodash";
import createCachedSelector from "re-reselect";
import { createSelector } from "reselect";

import { NARRATIVE_LABEL_AUTOMATIC } from "../../constants/narrativeAutomaticLabel";
import narrativeNumberingOptions from "../../constants/narrativeNumberingOptions";
import treeUtils from "../../utils/tree";
import { narrativeSettingsSelectors } from "../definition/narrativeSettings";
import { typeSelectors } from "../definition/types";
import { itemSelectors } from "../items";

const getNarrativeChildrenOfItem = (state, id) => treeUtils.getChildIdsOfParent(getNarrativeTree(state), id);
const itemHasNarrativeChildren = (state, id) => getNarrativeChildrenOfItem(state, id).length > 0;

const getNarrativePositionForItem = (narrativeTree, itemId) =>
  List(treeUtils.findPositionOfNode(narrativeTree, itemId));

const getNarrativeTree = state => state.data.narrative;

const getNarrativePositionForItemId = (state, itemId) => getNarrativePositionForItem(getNarrativeTree(state), itemId);

const getAllNarrativePositionsById = createSelector(
  // input
  narrativeTree => narrativeTree,
  // output
  narrativeTree => treeUtils.positionsForNodes(narrativeTree).map(a => List(a))
);

const getNarrativeIdsWithChildrenAsSet = createSelector(
  // input
  narrativeTree => narrativeTree,
  // output
  narrativeTree => {
    return Set(treeUtils.getIdsWithChildren(narrativeTree));
  }
);

const getAllNarrativePositionsByIdFromState = state => getAllNarrativePositionsById(getNarrativeTree(state));

/**
 * @param (TreeRecord) narrativeTree
 * @param (List) narrativeLabels
 * @param (Map) autoLabelsById
 */
const getContinuousNumberingById = createSelector(
  // input
  (narrativeTree, _narrativeLabels, _autoLabelsById) => narrativeTree,
  (narrativeTree, narrativeLabels, _autoLabelsById) => narrativeLabels,
  (narrativeTree, narrativeLabels, autoLabelsById) => autoLabelsById,

  // output
  (narrativeTree, narrativeLabels, autoLabelsById) => {
    let countPerLabel = {};
    let result = {};
    const narrativeIds = treeUtils.getIdsInTree(narrativeTree);
    narrativeIds.forEach(id => {
      const narrativeLabel = autoLabelsById.get(id);
      const labelObj = narrativeLabels.find(obj => obj.id === narrativeLabel);
      const index = countPerLabel[labelObj.id] ? countPerLabel[labelObj.id] + 1 : 1;
      result[id] = `${labelObj.label} ${index}`;
      countPerLabel[labelObj.id] = index;
    });
    return Map(result);
  }
);

const canBeAutoLabel = (narrativeLabel, isFolder) =>
  (isFolder && narrativeLabel.autoForFolder) || (!isFolder && narrativeLabel.autoForOther);

/**
 * Returns the automatic label for an item given its manual label and parent
 * @param {string} manualLabel
 * @param {string} parentLabel
 * @param {string} narrativeLabels
 * @param {boolean} isFolder
 * @returns string
 */
const getAutoLabel = (manualLabel, parentLabel, narrativeLabels, isFolder) => {
  if (manualLabel && manualLabel !== NARRATIVE_LABEL_AUTOMATIC) {
    return manualLabel;
  }
  if (parentLabel) {
    // get the index of the parent label
    const parentIndex = narrativeLabels.findIndex(l => l.id === parentLabel);
    // find the first index below that index that is allowed for this label
    let l = narrativeLabels.find((l, index) => index > parentIndex && canBeAutoLabel(l, isFolder));
    // if we don't find anything, find the first one at or above that index that is allowed
    if (!l) {
      l = narrativeLabels.findLast((l, index) => index <= parentIndex && canBeAutoLabel(l, isFolder));
    }
    if (l) {
      return l.id;
    }
  } else {
    const l = narrativeLabels.find(l => canBeAutoLabel(l, isFolder));
    if (l) {
      return l.id;
    }
  }

  // TODO: What do we do if nothing works?
  return "Unknown";
};

const getAutoLabelsInTree = (tree, parentLabel, itemsById, narrativeLabels, folderType) => {
  // for each child, work out its label
  const autoLabels = tree.children.reduce((result, childTree) => {
    const item = itemsById.get(childTree.id);
    const narrativeLabel = getAutoLabel(item.narrativeLabel, parentLabel, narrativeLabels, item.type === folderType);
    result[childTree.id] = narrativeLabel;
    const childIds = getAutoLabelsInTree(childTree, narrativeLabel, itemsById, narrativeLabels, folderType);
    return Object.assign(result, childIds);
  }, {});

  return autoLabels;
};

/**
 * @param (TreeRecord) narrativeTree
 * @param (List) narrativeLabels
 * @param (Map) itemsById
 * @param (string) folderTypeId
 */
const getNarrativeAutoLabelsById = createSelector(
  // input
  (narrativeTree, _narrativeLabels, _itemsById, _folderTypeId) => narrativeTree,
  (narrativeTree, narrativeLabels, _itemsById, _folderTypeId) => narrativeLabels,
  (narrativeTree, narrativeLabels, itemsById, _folderTypeId) => itemsById,
  (narrativeTree, narrativeLabels, itemsById, folderTypeId) => folderTypeId,
  // output
  (narrativeTree, narrativeLabels, itemsById, folderTypeId) => {
    return Map(getAutoLabelsInTree(narrativeTree, null, itemsById, narrativeLabels, folderTypeId));
  }
);

const getNarrativeAutoLabelsByIdFromState = state =>
  getNarrativeAutoLabelsById(
    getNarrativeTree(state),
    narrativeSettingsSelectors.getNarrativeLabels(state),
    itemSelectors.getItemsById(state),
    typeSelectors.getNarrativeFolderTypeId(state)
  );

/**
 * @param (TreeRecord) narrativeTree
 * @param (List) narrativeLabels
 * @param (Map) itemsById
 * @param (string) folderTypeId
 * @param (string) numberingSystem
 */
const getNarrativeDisplayLabelsById = createSelector(
  // input
  (narrativeTree, _narrativeLabels, _itemsById, _folderTypeId, _numberingSystem) => narrativeTree,
  (narrativeTree, narrativeLabels, _itemsById, _folderTypeId, _numberingSystem) => narrativeLabels,
  (narrativeTree, narrativeLabels, itemsById, folderTypeId, _numberingSystem) =>
    getNarrativeAutoLabelsById(narrativeTree, narrativeLabels, itemsById, folderTypeId),
  (narrativeTree, narrativeLabels, itemsById, _folderTypeId, numberingSystem) => numberingSystem,
  // output
  (narrativeTree, narrativeLabels, autoLabelsById, numberingSystem) => {
    if (numberingSystem === narrativeNumberingOptions.OUTLINE_STYLE) {
      const positionsById = getAllNarrativePositionsById(narrativeTree);
      return positionsById.map((position, id) => {
        const narrativeLabel = autoLabelsById.get(id);
        const labelObj = narrativeLabels.find(l => l.id === narrativeLabel);
        return `${labelObj.label} ${position.join(".")}`;
      });
    } else {
      return getContinuousNumberingById(narrativeTree, narrativeLabels, autoLabelsById);
    }
  }
);

const getNarrativeDisplayLabelsByIdFromState = state =>
  getNarrativeDisplayLabelsById(
    getNarrativeTree(state),
    narrativeSettingsSelectors.getNarrativeLabels(state),
    itemSelectors.getItemsById(state),
    typeSelectors.getNarrativeFolderTypeId(state),
    narrativeSettingsSelectors.getNarrativeNumbering(state)
  );

/**
 * @param (TreeRecord) narrativeTree
 * @param (List) narrativeLabels
 * @param (Map) itemsById
 * @param (string) folderTypeId
 * @param (string) numberingSystem
 */
const getNarrativePathLabelsById = createSelector(
  // input
  (narrativeTree, _narrativeLabels, _itemsById, _folderTypeId, _numberingSystem) => narrativeTree,
  (narrativeTree, narrativeLabels, itemsById, _folderTypeId, numberingSystem) => numberingSystem,
  getNarrativeDisplayLabelsById,
  // output
  (narrativeTree, numberingSystem, displayLabelsById) => {
    if (numberingSystem === narrativeNumberingOptions.OUTLINE_STYLE) {
      return displayLabelsById;
    } else {
      return displayLabelsById.map((displayLabel, id) => {
        const breadcrumbs = treeUtils.findBreadcrumbsOfNode(narrativeTree, id);
        let labels = map(breadcrumbs, id => displayLabelsById.get(id));
        return labels.join(", ");
      });
    }
  }
);

const getNarrativePathLabelsByIdFromState = state =>
  getNarrativePathLabelsById(
    getNarrativeTree(state),
    narrativeSettingsSelectors.getNarrativeLabels(state),
    itemSelectors.getItemsById(state),
    typeSelectors.getNarrativeFolderTypeId(state),
    narrativeSettingsSelectors.getNarrativeNumbering(state)
  );

const getAllNarrativeParentsById = createSelector(
  // input
  narrativeTree => narrativeTree,
  // output
  narrativeTree => Map(treeUtils.getParentAndIndexForAllIds(narrativeTree)).map(obj => obj.parent)
);

const getAllNarrativeParentsByIdFromState = state => getAllNarrativeParentsById(getNarrativeTree(state));

const getParentAndIndexForNarrativeIds = (state, ids) =>
  Map(treeUtils.getParentAndIndexForAllIds(getNarrativeTree(state))).filter((obj, id) => ids.indexOf(id) >= 0);

const getNarrativePositionForString = (narrativeLabels, str) => {
  // TODO: Probably want to improve this to pay attention to the actual text.
  // For now we will just rip out the numbers and assume it is correct.
  if (str) {
    const matchResult = str.match(/\d+/g);
    return List(matchResult ? matchResult.map(Number) : []);
  } else {
    return List([]);
  }
};

/**
 * If the two position represent the same container
 * In each level, the number should be the same except the last level
 * Eg. [1, 2] vs [1, 3] => true;[1, 1, 1] vs [1, 2, 1] => false
 */
const isSameContainer = (posA, posB) => {
  return (
    (posA.size === 1 && posB.size === 1) ||
    (posA.size === posB.size && posA.first() === posB.first() && isSameContainer(posA.shift(), posB.shift()))
  );
};

const getLastRootIndexForNarrative = state => getNarrativeTree(state).children.size - 1;

const idsInSubtree = subtree =>
  (subtree.id ? List([subtree.id]) : List()).push(subtree.children.map(childTree => idsInSubtree(childTree))).flatten();

const getNarrativeTreeIdsFromNarrative = narrativeTree => (narrativeTree ? idsInSubtree(narrativeTree) : List());

const getNarrativeTreeIds = createSelector(
  // inputs
  state => getNarrativeTree(state),
  // calculate based on inputs
  getNarrativeTreeIdsFromNarrative
);

const getNarrativeIdsBetweenIdsAsSet = createCachedSelector(
  // inputs
  (narrative, _from, _to, _filterId) => narrative,
  (narrative, from, _to, _filterId) => from,
  (narrative, from, to, _filterId) => to,
  // output
  (narrative, from, to) => {
    const narrativeIds = getNarrativeTreeIdsFromNarrative(narrative);
    let stopAt = to;
    if (to) {
      const toTree = treeUtils.getSubTreeWithId(narrative, to);
      const toIds = treeUtils.getIdsInTree(toTree);
      stopAt = last(toIds);
    }
    const startIndex = from ? narrativeIds.indexOf(from) : 0;
    const stopIndex = stopAt ? narrativeIds.indexOf(stopAt) + 1 : narrativeIds.size;
    return narrativeIds.slice(startIndex, stopIndex).toSet();
  }
)((narrative, from, to, filterId) => filterId);

const getNarrativeRootChildCount = state => getNarrativeTree(state).children.size;

const getNarrativeHasItems = state => getNarrativeTree(state).children.size > 0;
const getNarrativeHasMultipleItems = state => getNarrativeTree(state).children.size > 1;

const getNarrativeDepth = createSelector(
  // inputs
  getNarrativeTree,
  // output
  treeRoot => treeUtils.getTreeDepth(treeRoot)
);

const accumulateNarrativeAncestorsRecursive = (node, ancestors, result) => {
  // First, save the ancestors passed down to here.
  result[node.id] = ancestors;
  // Now add my own ID ready to pass to my children
  const nextAncestors = ancestors.add(node.id);
  // Now call for all children, passing along the set of their ancestors
  node.children.forEach(childNode => {
    accumulateNarrativeAncestorsRecursive(childNode, nextAncestors, result);
  });
};

const getNarrativeAncestorsFromState = createSelector(
  // input
  getNarrativeTree,
  // output
  narrative => {
    let result = {};
    // iterate through all top-level items
    // The children will be handled recursively
    narrative.children.forEach(node => accumulateNarrativeAncestorsRecursive(node, Set(), result));
    return Map(result);
  }
);

export default {
  getNarrativePositionForItem,
  getNarrativePositionForItemId,
  getNarrativePositionForString,
  getAllNarrativePositionsById,
  getAllNarrativePositionsByIdFromState,
  getNarrativeIdsWithChildrenAsSet,
  getAllNarrativeParentsById,
  getAllNarrativeParentsByIdFromState,
  getNarrativeAutoLabelsById,
  getNarrativeAutoLabelsByIdFromState,
  getNarrativeDisplayLabelsById,
  getNarrativeDisplayLabelsByIdFromState,
  getNarrativePathLabelsById,
  getNarrativePathLabelsByIdFromState,
  getNarrativeTree,
  isSameContainer,
  getLastRootIndexForNarrative,
  getNarrativeTreeIds,
  getNarrativeTreeIdsFromNarrative,
  getNarrativeIdsBetweenIdsAsSet,
  getParentAndIndexForNarrativeIds,
  getNarrativeChildrenOfItem,
  itemHasNarrativeChildren,
  getNarrativeRootChildCount,
  getNarrativeHasItems,
  getNarrativeHasMultipleItems,
  getNarrativeDepth,
  getNarrativeAncestorsFromState
};
